// =====================================================
// PROFILE SCREEN — allégé & optimisé Web (Hybrid Refresh)
// Imports partagés depuis :
//   - lib/feedHelpers.ts     → formatTimeAgo, getInitials, uriToBlob, uploadToStorage
//   - components/MediaViewers.tsx → SafeImage, MobileVideoPlayer, MediaViewer
//   - components/PostCard.tsx     → LikeButton
//   - components/LimitNotification.tsx
// Mises à jour :
//   ✅ Keyboard fix modal "Créer un post" (KeyboardAvoidingView + ScrollView interne)
//   ✅ Edit consolidé (ProfilePostCard + EditPostModal)
//   ✅ Pull-to-refresh élastique exclusif Mobile (iOS/Android)
//   ✅ Bouton de rafraîchissement natif dédié au Web
//   ✅ LogoutModal : flux mémorisation-avant-confirmation, navigation
//      unifiée dans onLogoutSuccess (plus de router.replace concurrent)
// Adapté Expo Router (tabs)/ :
//   ✅ Imports relatifs corrigés (../ → ../../)
//   ✅ Tab bar auto-hide synchronisée au scroll (useScrollAutoHide) — cet
//      écran n'a pas de header local animé, donc seule la tab bar globale
//      est pilotée (pas de onShow/onHide locaux nécessaires)
//
// PATCH i18n — Passage du texte affiché par i18next (useTranslation / t()).
// Icônes, couleurs, IDs, endpoints, clés AsyncStorage et tous les
// console.log restent codés en dur, inchangés.
// PATCH i18n — SettingsModal : ajout d'une section "Langue" détaillée
// (nom complet + nom natif de chaque langue + description d'instruction),
// utilisant exactement le même système que landing.tsx : i18n.changeLanguage
// (application immédiate) + saveLanguage (persistance), via useTranslation().
//
// PATCH STATUT EN LIGNE (design seul) — SettingsModal : ajout d'une carte
// "statut en ligne" entre la section Langue et les boutons d'action.
//   • AUCUN texte : uniquement des formes et des icônes (donc aucune clé
//     de traduction ajoutée).
//   • Rond de statut (vert avec halo pulsé = visible, gris barré = caché),
//     barres de signal (colorées = visible, grises = caché) et bouton
//     rond œil ouvert / œil barré pour basculer.
//   • BRANCHÉE sur le backend (route /revue) :
//       - à l'ouverture des paramètres, l'état réel est lu (getStatus) :
//         la carte affiche si le statut est caché ou non ;
//       - un tap sur l'œil envoie UN SEUL signal (hide = NULL, show =
//         on enlève le NULL) ; la carte ne change qu'après la réponse
//         du serveur.
//     Ces requêtes /revue sont exclues du signal de présence du layout.
// =====================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TextInput,
  TouchableOpacity, ActivityIndicator, Platform,
  KeyboardAvoidingView, Modal, Dimensions, Animated,
  RefreshControl,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient }    from 'expo-linear-gradient';
import { useRouter }         from 'expo-router';
import AsyncStorage          from '@react-native-async-storage/async-storage';
import * as ImagePicker      from 'expo-image-picker';
import DateTimePicker        from '@react-native-community/datetimepicker';
import * as Haptics          from 'expo-haptics';
import { useTranslation }    from 'react-i18next';
import { saveLanguage }      from '../../services/storage';

// ── Composants partagés ──────────────────────────────────────────────────────
import { SafeImage, MediaViewer }  from '../../components/MediaViewers';
import { LikeButton }              from '../../components/PostCard';
import LimitNotification           from '../../components/LimitNotification';
import {
  formatTimeAgo, getInitials,
  uriToBlob, uploadToStorage,
} from '../../lib/feedHelpers';

// ── Composants existants ─────────────────────────────────────────────────────
import CommentsModal   from '../../components/CommentsModal';
import LikersModal     from '../../components/LikersModal';
import SharersModal    from '../../components/SharersModal';
import EditPostModal   from '../../components/EditPostModal';
import ShareButton     from '../../components/ShareButton';
import LogoutModal     from '../../components/LogoutModal';
import AdminInfoModal  from '../../components/AdminInfoModal';

// ── Hook auto-hide (header local + tab bar globale synchronisés) ────────────
import { useScrollAutoHide } from '../../hooks/useScrollAutoHide';

// ─── Config ──────────────────────────────────────────────────────────────────
const WS_BASE     = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const PROFILE_URL = `${WS_BASE}/profile`;
const HOME_URL    = `${WS_BASE}/home`;
const REFRESH_URL = `${WS_BASE}/refresh-token`;
const REVUE_URL   = `${WS_BASE}/revue`;
const NATIVE      = Platform.OS !== 'web';

const BRAND_GOLD  = '#FFD700';
const PURPLE      = '#7B1FA2';
const PURPLE_DARK = '#4B0082';
const DARK_BG     = '#0D0D1A';
const CARD_BG     = '#14142B';
const CARD_BORDER = '#2A2A4A';
const TEXT_MAIN   = '#F0EFF8';
const TEXT_SUB    = '#9491B4';
const SUCCESS     = '#00E5A0';
const ACCENT      = '#7B5FFF';

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

// ─── Langues disponibles pour le sélecteur (même liste que landing.tsx) ────────
// Icônes/drapeaux/codes restent codés en dur ; seuls les libellés affichés
// (name / native) passent par t().
const LANGUAGES = [
  { code: 'fr', flag: '🇫🇷' },
  { code: 'en', flag: '🇬🇧' },
  { code: 'es', flag: '🇪🇸' },
  { code: 'pt', flag: '🇵🇹' },
];

// ─── Barres de signal de la carte statut (hauteurs en px, design seul) ─────────
const STATUS_BAR_HEIGHTS = [8, 14, 20, 26];

// ─── Types ───────────────────────────────────────────────────────────────────
type MessageType = 'success' | 'error' | 'info';
type Visibility  = 'public' | 'friends' | 'private' | 'pro';

interface MyProfile {
  id: string; nom: string; prenom: string;
  date_naissance: string | null;
  avatar_url?: string;
  solde_cfa: number; trophies_count: number;
  role: string; created_at: string;
}

interface Post {
  item_type: 'post'; id: string; author_id: string;
  author: { nom: string; prenom: string; avatar_url: string | null };
  content: string; visibility: string; created_at: string;
  imagepots?: string | null; vidposts?: string | null;
  reactions: { likes: number; comments: number; shares: number };
  user_liked?: boolean; user_shared?: boolean; user_saved?: boolean;
}

// ─── SettingsModal ────────────────────────────────────────────────────────────
function SettingsModal({ visible, profile, onClose, onSaved }: {
  visible: boolean; profile: MyProfile | null; onClose: () => void; onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const slideAnim = useRef(new Animated.Value(SCREEN_H)).current;
  const [nom,              setNom]              = useState('');
  const [prenom,           setPrenom]           = useState('');
  const [dateNaissance,    setDateNaissance]    = useState<Date>(new Date(2000, 0, 1));
  const [dateNaissanceWeb, setDateNaissanceWeb] = useState('');
  const [showDatePicker,   setShowDatePicker]   = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [toast,            setToast]            = useState<string | null>(null);
  const [changingLanguage, setChangingLanguage] = useState<string | null>(null);

  // ── Statut en ligne (branché sur /revue) ──────────────────────────────────
  // statusVisible : true = statut montré, false = statut caché (NULL en base)
  // statusLoading : lecture de l'état réel à l'ouverture des paramètres
  // statusSaving  : envoi du signal hide/show en cours
  const [statusVisible, setStatusVisible] = useState(true);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusSaving,  setStatusSaving]  = useState(false);
  const haloAnim = useRef(new Animated.Value(0)).current;
  const orbScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (visible) {
      setNom(profile?.nom || '');
      setPrenom(profile?.prenom || '');
      if (profile?.date_naissance) {
        const d = new Date(profile.date_naissance);
        setDateNaissance(d);
        setDateNaissanceWeb(profile.date_naissance);
      }
      Animated.spring(slideAnim, { toValue: 0, friction: 9, tension: 60, useNativeDriver: true }).start();
    } else {
      Animated.timing(slideAnim, { toValue: SCREEN_H, duration: 280, useNativeDriver: true }).start();
    }
  }, [visible]);

  // Halo pulsé du rond de statut : uniquement quand la fenêtre est ouverte
  // ET que le statut est "visible". Arrêté et remis à zéro sinon.
  useEffect(() => {
    if (!visible || !statusVisible || statusLoading) {
      haloAnim.stopAnimation();
      haloAnim.setValue(0);
      return;
    }
    const loop = Animated.loop(
      Animated.timing(haloAnim, { toValue: 1, duration: 1500, useNativeDriver: true })
    );
    loop.start();
    return () => { loop.stop(); haloAnim.setValue(0); };
  }, [visible, statusVisible, statusLoading]);

  // Jeton de la session (même lecture que les autres actions de ce modal)
  const getSessionToken = async (): Promise<string | null> => {
    try {
      const raw = await AsyncStorage.getItem('harmonia_session');
      return raw ? (JSON.parse(raw).access_token ?? null) : null;
    } catch { return null; }
  };

  // Lecture de l'état réel : caché (NULL en base) ou non.
  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const token = await getSessionToken();
      if (!token) throw new Error('no_token');
      const res  = await fetch(REVUE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getStatus', access_token: token }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error);
      setStatusVisible(!data.hidden);
    } catch {
      showLocalToast(t('profileScreen.settings.errors.cannotSave'));
    } finally {
      setStatusLoading(false);
    }
  };

  // Un tap = UN SEUL signal : hide (met NULL) ou show (enlève le NULL).
  // La carte ne change d'aspect qu'après la réponse du serveur.
  const handleToggleStatus = async () => {
    if (statusLoading || statusSaving) return;
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const next = !statusVisible;
    setStatusSaving(true);
    try {
      const token = await getSessionToken();
      if (!token) throw new Error('no_token');
      const res  = await fetch(REVUE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: next ? 'show' : 'hide', access_token: token }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error);
      setStatusVisible(next);
      Animated.sequence([
        Animated.timing(orbScale, { toValue: 0.8, duration: 90, useNativeDriver: true }),
        Animated.spring(orbScale, { toValue: 1, friction: 4, tension: 120, useNativeDriver: true }),
      ]).start();
    } catch {
      showLocalToast(t('profileScreen.settings.errors.cannotSave'));
    } finally {
      setStatusSaving(false);
    }
  };

  // À chaque ouverture des paramètres, on relit l'état réel
  useEffect(() => {
    if (visible) loadStatus();
  }, [visible]);

  const showLocalToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const handleSave = async () => {
    if (!nom.trim() || !prenom.trim()) { showLocalToast(t('profileScreen.settings.errors.nameRequired')); return; }
    setSaving(true);
    try {
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (!raw) return;
      const token = JSON.parse(raw).access_token;
      const dateForAPI = Platform.OS === 'web'
        ? dateNaissanceWeb
        : `${dateNaissance.getFullYear()}-${(dateNaissance.getMonth()+1).toString().padStart(2,'0')}-${dateNaissance.getDate().toString().padStart(2,'0')}`;
      const res  = await fetch(`${WS_BASE}/profile`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update-profile', access_token: token, nom: nom.trim(), prenom: prenom.trim(), date_naissance: dateForAPI }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onSaved(); onClose();
    } catch (err: any) { showLocalToast(err.message || t('profileScreen.settings.errors.cannotSave')); }
    finally { setSaving(false); }
  };

  const formatDateDisplay = (d: Date) =>
    `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear()}`;

  const onDateChange = (_event: any, selectedDate?: Date) => {
    setShowDatePicker(Platform.OS === 'ios');
    if (selectedDate) setDateNaissance(selectedDate);
  };

  // ── Changement de langue ────────────────────────────────────────────────
  // Même système que landing.tsx : application immédiate via
  // i18n.changeLanguage, puis persistance via saveLanguage. La sélection
  // n'attend pas "Enregistrer" — elle prend effet dès le tap, comme le
  // reste de l'app.
  // PATCH DB — après la persistance locale, on synchronise aussi la colonne
  // `langue` côté Supabase, via la route existante /profile action
  // update-profile, avec le JWT de l'utilisateur (access_token). Payload
  // langue-seule : n'envoie ni nom, ni prenom, ni date_naissance — le
  // backend accepte désormais chaque champ de façon indépendante.
  const handleChangeLanguage = async (code: string) => {
    if (code === i18n.language) return;
    setChangingLanguage(code);
    try {
      await i18n.changeLanguage(code);
      await saveLanguage(code);

      const raw = await AsyncStorage.getItem('harmonia_session');
      if (raw) {
        const token = JSON.parse(raw).access_token;
        await fetch(`${WS_BASE}/profile`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'update-profile', access_token: token, langue: code }),
        });
      }
    } catch {
      showLocalToast(t('profileScreen.settings.errors.cannotChangeLanguage'));
    } finally {
      setChangingLanguage(null);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <Animated.View style={[sStyles.container, { transform: [{ translateY: slideAnim }] }]}>
          <LinearGradient colors={[PURPLE, PURPLE_DARK]} style={sStyles.header}>
            <TouchableOpacity style={sStyles.backBtn} onPress={onClose} activeOpacity={0.75}>
              <Ionicons name="arrow-back" size={22} color="#fff" />
            </TouchableOpacity>
            <View style={sStyles.headerCenter}>
              <View style={sStyles.headerIconBox}>
                <Ionicons name="settings" size={26} color={BRAND_GOLD} />
              </View>
              <Text style={sStyles.headerTitle}>{t('profileScreen.settings.header.title')}</Text>
              <Text style={sStyles.headerSub}>{t('profileScreen.settings.header.subtitle')}</Text>
            </View>
          </LinearGradient>

          {toast && (
            <View style={sStyles.toast}>
              <Ionicons name="alert-circle-outline" size={16} color="#fff" />
              <Text style={sStyles.toastText}>{toast}</Text>
            </View>
          )}

          <ScrollView style={{ flex: 1, backgroundColor: DARK_BG }} contentContainerStyle={sStyles.scroll} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {/* Nom */}
            <View style={sStyles.fieldBlock}>
              <Text style={sStyles.fieldLabel}>{t('profileScreen.settings.fields.lastName')}</Text>
              <View style={sStyles.inputRow}>
                <View style={sStyles.inputIconBox}><Ionicons name="person-outline" size={18} color={BRAND_GOLD} /></View>
                <TextInput style={sStyles.input} value={nom} onChangeText={setNom} placeholder={t('profileScreen.settings.fields.lastNamePlaceholder')} placeholderTextColor={TEXT_SUB} autoCapitalize="words" />
              </View>
            </View>
            {/* Prénom */}
            <View style={sStyles.fieldBlock}>
              <Text style={sStyles.fieldLabel}>{t('profileScreen.settings.fields.firstName')}</Text>
              <View style={sStyles.inputRow}>
                <View style={sStyles.inputIconBox}><Ionicons name="person-outline" size={18} color={BRAND_GOLD} /></View>
                <TextInput style={sStyles.input} value={prenom} onChangeText={setPrenom} placeholder={t('profileScreen.settings.fields.firstNamePlaceholder')} placeholderTextColor={TEXT_SUB} autoCapitalize="words" />
              </View>
            </View>
            {/* Date */}
            <View style={sStyles.fieldBlock}>
              <Text style={sStyles.fieldLabel}>{t('profileScreen.settings.fields.birthDate')}</Text>
              {Platform.OS === 'web' ? (
                <View style={sStyles.inputRow}>
                  <View style={sStyles.inputIconBox}><Ionicons name="calendar-outline" size={18} color={BRAND_GOLD} /></View>
                  {/* @ts-ignore */}
                  <input type="date" value={dateNaissanceWeb} onChange={(e: any) => setDateNaissanceWeb(e.target.value)} max={new Date().toISOString().split('T')[0]} min="1900-01-01" style={{ flex: 1, padding: 14, fontSize: 15, border: 'none', outline: 'none', backgroundColor: 'transparent', color: TEXT_MAIN }} />
                </View>
              ) : (
                <>
                  <TouchableOpacity style={sStyles.inputRow} onPress={() => setShowDatePicker(true)}>
                    <View style={sStyles.inputIconBox}><Ionicons name="calendar-outline" size={18} color={BRAND_GOLD} /></View>
                    <Text style={[sStyles.input, { paddingVertical: 15, color: TEXT_MAIN }]}>{formatDateDisplay(dateNaissance)}</Text>
                    <Ionicons name="chevron-down-outline" size={18} color={TEXT_SUB} style={{ marginRight: 14 }} />
                  </TouchableOpacity>
                  {showDatePicker && (
                    <DateTimePicker value={dateNaissance} mode="date" display={Platform.OS === 'ios' ? 'spinner' : 'default'} onChange={onDateChange} maximumDate={new Date()} minimumDate={new Date(1900, 0, 1)} />
                  )}
                </>
              )}
            </View>

            {/* ── Langue ──────────────────────────────────────────────────
                Section détaillée : titre, texte d'instruction complet, puis
                une ligne par langue avec drapeau + nom complet + nom natif
                + coche sur la langue active. Application immédiate au tap
                (pas d'attente du bouton "Enregistrer"). */}
            <View style={sStyles.fieldBlock}>
              <Text style={sStyles.fieldLabel}>{t('profileScreen.settings.language.label')}</Text>
              <Text style={sStyles.languageIntro}>{t('profileScreen.settings.language.intro')}</Text>
              <View style={sStyles.languageList}>
                {LANGUAGES.map((lang, index) => {
                  const isActive  = lang.code === i18n.language;
                  const isLoading = changingLanguage === lang.code;
                  return (
                    <TouchableOpacity
                      key={lang.code}
                      style={[
                        sStyles.languageRow,
                        index < LANGUAGES.length - 1 && sStyles.languageRowBorder,
                        isActive && sStyles.languageRowActive,
                      ]}
                      onPress={() => handleChangeLanguage(lang.code)}
                      activeOpacity={0.75}
                      disabled={isLoading}
                    >
                      <Text style={sStyles.languageFlag}>{lang.flag}</Text>
                      <View style={{ flex: 1 }}>
                        <Text style={[sStyles.languageName, isActive && sStyles.languageNameActive]}>
                          {t(`profileScreen.settings.language.options.${lang.code}.name`)}
                        </Text>
                        <Text style={sStyles.languageNative}>
                          {t(`profileScreen.settings.language.options.${lang.code}.native`)}
                        </Text>
                      </View>
                      {isLoading ? (
                        <ActivityIndicator size="small" color={BRAND_GOLD} />
                      ) : isActive ? (
                        <View style={sStyles.languageCheckBox}>
                          <Ionicons name="checkmark" size={16} color={DARK_BG} />
                        </View>
                      ) : (
                        <View style={sStyles.languageCheckBoxEmpty} />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {/* ── Statut en ligne (aucun texte) ───────────────────────────
                Rond de statut + barres de signal + bouton œil, branchés sur
                l'état réel (route /revue).
                Visible  → rond vert avec halo pulsé, barres vertes, œil ouvert.
                Caché    → rond gris barré, barres grises, œil barré.
                Pendant la lecture de l'état réel, la carte est atténuée. */}
            <View style={[sStyles.statusCard, statusLoading && sStyles.statusCardLoading]}>
              <Animated.View style={[sStyles.statusOrbWrap, { transform: [{ scale: orbScale }] }]}>
                {statusVisible && (
                  <Animated.View
                    style={[
                      sStyles.statusHalo,
                      {
                        opacity:   haloAnim.interpolate({ inputRange: [0, 1], outputRange: [0.5, 0] }),
                        transform: [{ scale: haloAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 2.1] }) }],
                      },
                    ]}
                  />
                )}
                <View style={[sStyles.statusOrb, statusVisible ? sStyles.statusOrbOn : sStyles.statusOrbOff]}>
                  {!statusVisible && <View style={sStyles.statusSlash} />}
                </View>
              </Animated.View>

              <View style={sStyles.statusBars}>
                {STATUS_BAR_HEIGHTS.map((h, i) => (
                  <View
                    key={i}
                    style={[
                      sStyles.statusBar,
                      { height: h },
                      statusVisible ? sStyles.statusBarOn : sStyles.statusBarOff,
                    ]}
                  />
                ))}
              </View>

              <TouchableOpacity
                style={[sStyles.statusBtn, statusVisible ? sStyles.statusBtnOn : sStyles.statusBtnOff]}
                onPress={handleToggleStatus}
                disabled={statusLoading || statusSaving}
                activeOpacity={0.8}
                hitSlop={8}
                accessibilityRole="switch"
                accessibilityState={{ checked: statusVisible, disabled: statusLoading || statusSaving }}
              >
                {statusLoading || statusSaving ? (
                  <ActivityIndicator size="small" color={TEXT_SUB} />
                ) : (
                  <Ionicons
                    name={statusVisible ? 'eye-outline' : 'eye-off-outline'}
                    size={22}
                    color={statusVisible ? SUCCESS : TEXT_SUB}
                  />
                )}
              </TouchableOpacity>
            </View>

            {/* Actions */}
            <View style={sStyles.actions}>
              <TouchableOpacity style={sStyles.cancelBtn} onPress={onClose} activeOpacity={0.75}>
                <Text style={sStyles.cancelText}>{t('profileScreen.settings.actions.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={sStyles.saveBtn} onPress={handleSave} disabled={saving} activeOpacity={0.8}>
                <LinearGradient colors={[PURPLE, PURPLE_DARK]} start={{ x:0,y:0 }} end={{ x:1,y:0 }} style={sStyles.saveBtnGrad}>
                  {saving
                    ? <ActivityIndicator size="small" color="#fff" />
                    : <><Ionicons name="checkmark" size={18} color="#fff" /><Text style={sStyles.saveText}>{t('profileScreen.settings.actions.save')}</Text></>
                  }
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
}

// ─── PostCard (version profil) ────────────────────────────────────────────────
const ProfilePostCard = React.memo(({
  post, userId, isPro, onLike, onShare, onSave,
  onOpenComments, onOpenLikers, onOpenSharers, onOpenEdit, onLongPress,
}: {
  post: Post; userId: string; isPro: boolean;
  onLike:         (postId: string, liked: boolean)  => Promise<void>;
  onShare:        (postId: string, shared: boolean) => Promise<void>;
  onSave:         (postId: string, saved: boolean)  => Promise<void>;
  onOpenComments: (postId: string) => void;
  onOpenLikers:   (postId: string) => void;
  onOpenSharers:  (postId: string) => void;
  onOpenEdit:     (post: { id: string; content: string }) => void;
  onLongPress:    (post: Post) => void;
}) => {
  const { t } = useTranslation();
  const [liked,       setLiked]       = useState(post.user_liked  || false);
  const [shared,      setShared]      = useState(post.user_shared || false);
  const [saved,       setSaved]       = useState(post.user_saved  || false);
  const [likesCount,  setLikesCount]  = useState(post.reactions.likes);
  const [sharesCount, setSharesCount] = useState(post.reactions.shares);
  const [isAnim,      setIsAnim]      = useState(false);
  const [viewerUrl,   setViewerUrl]   = useState<string | null>(null);
  const [viewerType,  setViewerType]  = useState<'image' | 'video'>('image');

  useEffect(() => {
    setLiked(post.user_liked   || false);
    setShared(post.user_shared || false);
    setSaved(post.user_saved   || false);
    setLikesCount(post.reactions.likes);
    setSharesCount(post.reactions.shares);
  }, [post.id, post.user_liked, post.user_shared, post.user_saved, post.reactions]);

  const handleLike = async () => {
    if (isAnim) return;
    setIsAnim(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    const n = !liked; setLiked(n); setLikesCount(p => n ? p+1 : p-1);
    try { await onLike(post.id, n); }
    catch { setLiked(!n); setLikesCount(p => n ? p-1 : p+1); }
    finally { setTimeout(() => setIsAnim(false), 300); }
  };

  const handleShareLocal = async (postId: string, sh: boolean) => {
    const n = sh; setShared(n); setSharesCount(p => n ? p+1 : p-1);
    try { await onShare(postId, n); }
    catch { setShared(!n); setSharesCount(p => n ? p-1 : p+1); }
  };

  const handleSave = async () => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const n = !saved; setSaved(n);
    try { await onSave(post.id, n); }
    catch { setSaved(!n); }
  };

  return (
    <>
      <MediaViewer visible={!!viewerUrl} url={viewerUrl} type={viewerType} onClose={() => setViewerUrl(null)} />
      <TouchableOpacity style={styles.postCard} activeOpacity={1} onLongPress={() => onLongPress(post)}>

        {/* Header */}
        <View style={styles.postHeader}>
          <View style={styles.postAuthor}>
            <SafeImage uri={post.author.avatar_url} style={styles.postAvatar} resizeMode="cover"
              fallback={<View style={styles.avatarPlaceholder}><Text style={styles.avatarText}>{getInitials(post.author.nom, post.author.prenom)}</Text></View>}
            />
            <View>
              <Text style={styles.authorName}>{post.author.prenom} {post.author.nom}</Text>
              <Text style={styles.postTime}>{formatTimeAgo(post.created_at)}</Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <TouchableOpacity onPress={() => onOpenEdit({ id: post.id, content: post.content })}>
              <Ionicons name="pencil-outline" size={18} color="#666" />
            </TouchableOpacity>
          </View>
        </View>

        <Text style={styles.postContent}>{post.content}</Text>

        {post.imagepots ? (
          <TouchableOpacity activeOpacity={0.92} onPress={() => { setViewerType('image'); setViewerUrl(post.imagepots!); }}>
            <SafeImage uri={post.imagepots} style={styles.postImage} resizeMode="cover" />
            <View style={styles.mediaExpandHint}><Ionicons name="expand-outline" size={16} color="rgba(255,255,255,0.85)" /></View>
          </TouchableOpacity>
        ) : null}

        {post.vidposts ? (
          <TouchableOpacity style={styles.videoThumb} activeOpacity={0.92} onPress={() => { setViewerType('video'); setViewerUrl(post.vidposts!); }}>
            <Ionicons name="play-circle" size={52} color="rgba(255,255,255,0.9)" />
            <Text style={styles.videoLabel}>{t('profileScreen.post.tapToPlay')}</Text>
          </TouchableOpacity>
        ) : null}

        {/* Stats bar */}
        <View style={styles.statsBar}>
          <TouchableOpacity onPress={() => { if (likesCount > 0) onOpenLikers(post.id); }}>
            {likesCount > 0 ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <View style={styles.statsLikeCircle}><MaterialCommunityIcons name="thumb-up" size={10} color="#FFF" /></View>
                <Text style={styles.statsText}>{likesCount}</Text>
              </View>
            ) : <Text style={styles.statsText}></Text>}
          </TouchableOpacity>
          <View style={styles.statsRight}>
            {post.reactions.comments > 0 && <Text style={styles.statsText}>{t('profileScreen.post.commentsCount', { count: post.reactions.comments })}</Text>}
            {post.reactions.comments > 0 && sharesCount > 0 && <Text style={styles.statsSep}>•</Text>}
            {sharesCount > 0 && (
              <TouchableOpacity onPress={() => onOpenSharers(post.id)}>
                <Text style={[styles.statsText, styles.statsSharesLink]}>{t('profileScreen.post.sharesCount', { count: sharesCount })}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>

        {/* Actions bar */}
        <View style={styles.actionsBar}>
          <LikeButton liked={liked} isAnim={isAnim} onPress={handleLike} />
          <TouchableOpacity style={styles.actionBtnModern} onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); onOpenComments(post.id); }} activeOpacity={0.6}>
            <View style={styles.actionContentModern}><Ionicons name="chatbubble-outline" size={22} color="#65676B" /></View>
          </TouchableOpacity>
          {(post.visibility === 'public' || post.visibility === 'pro') && (
            <ShareButton postId={post.id} content={post.content} isPro={post.visibility === 'pro'} shared={shared} sharesCount={sharesCount} onShareHarmonia={handleShareLocal} />
          )}
          <TouchableOpacity style={[styles.actionBtnModern, { flex: 0, paddingHorizontal: 12 }]} onPress={handleSave} activeOpacity={0.6}>
            <View style={styles.actionContentModern}>
              <Ionicons name={saved ? 'bookmark' : 'bookmark-outline'} size={22} color={saved ? BRAND_GOLD : '#65676B'} />
            </View>
          </TouchableOpacity>
        </View>
      </TouchableOpacity>
    </>
  );
});

// ─── Sous-composants ─────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <View style={styles.infoRow}>
      <View style={styles.infoIconBox}><Ionicons name={icon} size={20} color={PURPLE} /></View>
      <View style={{ flex: 1 }}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
    </View>
  );
}
function Separator() { return <View style={styles.separatorLine} />; }

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export default function ProfileScreen() {
  const { t } = useTranslation();
  const router = useRouter();

  const [profile,         setProfile]         = useState<MyProfile | null>(null);
  const [posts,           setPosts]           = useState<Post[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [loadingPosts,    setLoadingPosts]    = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [toast,           setToast]           = useState<{ type: MessageType; text: string } | null>(null);
  const [showAvatarViewer,setShowAvatarViewer]= useState(false);
  const [showCreatePost,  setShowCreatePost]  = useState(false);
  const [userId,          setUserId]          = useState('');

  // ── Pull-to-refresh ──────────────────────────────────────────────────────────
  const [refreshing, setRefreshing] = useState(false);

  const [selectedPost,            setSelectedPost]            = useState<Post | null>(null);
  const [showCommentsModal,       setShowCommentsModal]       = useState(false);
  const [selectedPostForComments, setSelectedPostForComments] = useState<string | null>(null);
  const [showLikersModal,         setShowLikersModal]         = useState(false);
  const [selectedPostForLikers,   setSelectedPostForLikers]   = useState<string | null>(null);
  const [showSharersModal,        setShowSharersModal]        = useState(false);
  const [selectedPostForSharers,  setSelectedPostForSharers]  = useState<string | null>(null);
  const [showEditModal,           setShowEditModal]           = useState(false);
  const [selectedPostForEdit,     setSelectedPostForEdit]     = useState<{ id: string; content: string } | null>(null);
  const [showLogoutModal,         setShowLogoutModal]         = useState(false);
  const [showAdminInfo,           setShowAdminInfo]           = useState(false);
  const [limitNotif,              setLimitNotif]              = useState(false);
  const [limitNotifMessage,       setLimitNotifMessage]       = useState('');
  const [showSettings,            setShowSettings]            = useState(false);

  const [postContent,    setPostContent]    = useState('');
  const [postVisibility, setPostVisibility] = useState<Visibility>('friends');
  const [creatingPost,   setCreatingPost]   = useState(false);
  const [postImageUrl,   setPostImageUrl]   = useState<string | null>(null);
  const [postVideoUrl,   setPostVideoUrl]   = useState<string | null>(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const accessTokenRef  = useRef<string>('');
  const refreshTokenRef = useRef<string>('');
  const expiresAtRef    = useRef<number>(0);

  // ── Tab bar auto-hide au scroll ──────────────────────────────────────────
  // Cet écran n'a pas de header local animé (contrairement à actu.tsx),
  // donc on branche le hook sans onShow/onHide — seule la tab bar globale
  // du (tabs)/_layout.tsx sera cachée/montrée selon le scroll.
  const { handleScroll, handleDoubleTap } = useScrollAutoHide();

  useEffect(() => { initSession(); }, []);

  const initSession = async () => {
    try {
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (!raw) { router.replace('/login'); return; }
      const session = JSON.parse(raw);
      accessTokenRef.current  = session.access_token  || '';
      refreshTokenRef.current = session.refresh_token || '';
      expiresAtRef.current    = session.expires_at    || 0;
      setUserId(session?.user?.id || '');
      await Promise.all([loadProfile(), loadPosts()]);
    } catch { router.replace('/login'); }
  };

  const getValidToken = async (): Promise<string> => {
    const now = Math.floor(Date.now() / 1000);
    if (expiresAtRef.current - now > 60) return accessTokenRef.current;
    try {
      const res  = await fetch(REFRESH_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshTokenRef.current }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      accessTokenRef.current  = data.access_token;
      refreshTokenRef.current = data.refresh_token;
      expiresAtRef.current    = data.expires_at;
      const raw = await AsyncStorage.getItem('harmonia_session');
      if (raw) await AsyncStorage.setItem('harmonia_session', JSON.stringify({
        ...JSON.parse(raw), access_token: data.access_token,
        refresh_token: data.refresh_token, expires_at: data.expires_at,
      }));
      return data.access_token;
    } catch { router.replace('/login'); return ''; }
  };

  const showToast = (type: MessageType, text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const token = await getValidToken(); if (!token) return;
      const res  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-profile', access_token: token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setProfile(data.profile);
    } catch (err: any) { showToast('error', err.message || t('profileScreen.errors.cannotLoadProfile')); }
    finally { setLoading(false); }
  };

  const loadPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      const token = await getValidToken(); if (!token) return;
      const res  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-posts', access_token: token }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setPosts(data.posts || []);
    } catch (err: any) { console.error('[loadPosts]', err.message); }
    finally { setLoadingPosts(false); }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    try {
      await Promise.all([loadProfile(), loadPosts()]);
    } finally {
      setRefreshing(false);
    }
  }, [loadPosts]);

  const handleLike = useCallback(async (postId: string, liked: boolean) => {
    const token = await getValidToken(); if (!token) return;
    const res   = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: liked ? 'like-post' : 'unlike-post', user_id: userId, access_token: token, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: { ...p.reactions, likes: data.likes }, user_liked: liked } : p
    ));
  }, [userId]);

  const handleShare = useCallback(async (postId: string, shared: boolean) => {
    const token = await getValidToken(); if (!token) return;
    const res   = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: shared ? 'share-post' : 'unshare-post', user_id: userId, access_token: token, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, reactions: { ...p.reactions, shares: data.shares }, user_shared: shared } : p
    ));
  }, [userId]);

  const handleSave = useCallback(async (postId: string, saved: boolean) => {
    const token = await getValidToken(); if (!token) return;
    const res   = await fetch(HOME_URL, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: saved ? 'save-post' : 'unsave-post', user_id: userId, access_token: token, post_id: postId }),
    });
    const data = await res.json();
    if (data.success) setPosts(prev => prev.map(p =>
      p.id === postId ? { ...p, user_saved: saved } : p
    ));
  }, [userId]);

  const handleCreatePost = async () => {
    if (!postContent.trim()) { showToast('error', t('profileScreen.createPost.errors.contentRequired')); return; }
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setCreatingPost(true);
    try {
      const token = await getValidToken(); if (!token) return;
      const res  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create-post', access_token: token,
          content: postContent.trim(), visibility: postVisibility,
          imagepots: postImageUrl || undefined, vidposts: postVideoUrl || undefined,
        }),
      });
      const data = await res.json();
      if (res.status === 429) {
        setShowCreatePost(false); setPostContent('');
        setLimitNotifMessage(data.error || t('profileScreen.createPost.errors.limitReached'));
        setLimitNotif(true); return;
      }
      if (!res.ok) throw new Error(data.error);
      setPostContent(''); setPostVisibility('friends');
      setPostImageUrl(null); setPostVideoUrl(null);
      setShowCreatePost(false);
      await loadPosts();
      showToast('success', t('profileScreen.createPost.published'));
    } catch (err: any) { showToast('error', err.message || t('profileScreen.createPost.errors.cannotPublish')); }
    finally { setCreatingPost(false); }
  };

  const handlePickMedia = async (mediaType: 'image' | 'video') => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') { showToast('error', t('profileScreen.createPost.errors.mediaAccessRequired')); return; }
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: mediaType === 'video' ? ImagePicker.MediaTypeOptions.Videos : ImagePicker.MediaTypeOptions.Images,
      allowsEditing: mediaType === 'image', quality: 0.8,
    });
    if (result.canceled || !result.assets[0]) return;
    setUploadingMedia(true);
    try {
      const token   = await getValidToken(); if (!token) return;
      const urlRes  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-upload-url', access_token: token, context: mediaType === 'image' ? 'image' : 'video' }),
      });
      const urlData = await urlRes.json();
      if (!urlRes.ok) throw new Error(urlData.error);
      await uploadToStorage(urlData.signed_url, result.assets[0].uri);
      if (mediaType === 'image') setPostImageUrl(urlData.public_url);
      else                       setPostVideoUrl(urlData.public_url);
      showToast('success', mediaType === 'image' ? t('profileScreen.createPost.imageAdded') : t('profileScreen.createPost.videoAdded'));
    } catch (err: any) { showToast('error', err.message || t('profileScreen.createPost.errors.cannotAddMedia')); }
    finally { setUploadingMedia(false); }
  };

  const pickAndUploadAvatar = async () => {
    if (Platform.OS !== 'web') {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') { showToast('error', t('profileScreen.avatar.errors.accessRequired')); return; }
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images, allowsEditing: true, aspect: [1,1], quality: 0.8,
    });
    if (result.canceled || !result.assets[0]) return;
    setUploadingAvatar(true);
    try {
      const token       = await getValidToken(); if (!token) return;
      const urlRes      = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get-upload-url', access_token: token, context: 'avatar' }),
      });
      const urlData     = await urlRes.json();
      if (!urlRes.ok) throw new Error(urlData.error);
      await uploadToStorage(urlData.signed_url, result.assets[0].uri);
      const confirmRes  = await fetch(PROFILE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'confirm-upload', access_token: token, context: 'avatar', path: urlData.path }),
      });
      const confirmData = await confirmRes.json();
      if (!confirmRes.ok) throw new Error(confirmData.error);
      await loadProfile();
      showToast('success', t('profileScreen.avatar.updated'));
    } catch (err: any) { showToast('error', err.message || t('profileScreen.avatar.errors.cannotUpdate')); }
    finally { setUploadingAvatar(false); }
  };

  const handleOpenComments = useCallback((postId: string) => {
    if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setSelectedPostForComments(postId); setShowCommentsModal(true);
  }, []);
  const handleOpenLikers  = useCallback((postId: string) => { setSelectedPostForLikers(postId);  setShowLikersModal(true);  }, []);
  const handleOpenSharers = useCallback((postId: string) => { setSelectedPostForSharers(postId); setShowSharersModal(true); }, []);
  const handleOpenEdit    = useCallback((post: { id: string; content: string }) => { setSelectedPostForEdit(post); setShowEditModal(true); }, []);
  const handleLongPress   = useCallback((post: Post) => { setSelectedPost(post); }, []);

  // ── Déconnexion ────────────────────────────────────────────────────────────
  // LogoutModal ne navigue plus lui-même : il fait tout le travail de données
  // (appel API /logout, sauvegarde éventuelle du compte, nettoyage de la
  // session) puis remonte un seul résultat via onLogoutSuccess(wantsToRemember).
  // C'est ICI, et seulement ici, que la navigation finale est décidée — pour
  // éviter deux router.replace concurrents qui s'écrasaient l'un l'autre.
  const handleLogoutSuccess = useCallback((wantsToRemember: boolean) => {
    if (wantsToRemember) {
      router.replace('/saved-accounts');
    } else {
      router.replace('/login');
    }
  }, [router]);

  const isPro   = profile?.role === 'userpro';
  const isAdmin = ['admin', 'adminpro', 'supreme'].includes(profile?.role ?? '');
  const visibilitiesAllowed: Visibility[] = isPro ? ['public','friends','private','pro'] : ['public','friends','private'];
  const formatDateReadable = (iso: string) =>
    !iso ? '—' : new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  const ACCOUNT_BTNS = [
    { icon: 'settings-outline',         iconLib: 'Ionicons',               color: PURPLE,  bgColor: PURPLE+'22',  title: t('profileScreen.account.settings.title'),   sub: t('profileScreen.account.settings.sub'),   onPress: () => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); setShowSettings(true); } },
    { icon: 'shield-checkmark-outline', iconLib: 'Ionicons',               color: ACCENT,  bgColor: ACCENT+'22',  title: t('profileScreen.account.privacy.title'),    sub: t('profileScreen.account.privacy.sub'),    onPress: () => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); router.push('/confidentialite'); } },
    { icon: 'book-open-variant',        iconLib: 'MaterialCommunityIcons', color: SUCCESS, bgColor: SUCCESS+'22', title: t('profileScreen.account.guide.title'),      sub: t('profileScreen.account.guide.sub'),      onPress: () => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); router.push('/guide'); } },
  ];

  if (loading) return (
    <View style={styles.centered}>
      <ActivityIndicator size="large" color={PURPLE} />
      <Text style={styles.loadingText}>{t('profileScreen.loading')}</Text>
    </View>
  );

  if (!profile) return (
    <View style={styles.centered}>
      <Ionicons name="alert-circle-outline" size={64} color="#EF4444" />
      <Text style={styles.errorText}>{t('profileScreen.errors.cannotLoadProfile')}</Text>
      <TouchableOpacity onPress={loadProfile} style={styles.retryButton}>
        <Text style={styles.retryButtonText}>{t('profileScreen.retry')}</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>

      <SettingsModal visible={showSettings} profile={profile} onClose={() => setShowSettings(false)} onSaved={async () => { await loadProfile(); showToast('success', t('profileScreen.settings.updated')); }} />

      {/* Viewer avatar */}
      <Modal visible={showAvatarViewer} transparent animationType="fade" onRequestClose={() => setShowAvatarViewer(false)} statusBarTranslucent>
        <View style={styles.mediaViewerOverlay}>
          <TouchableOpacity style={styles.mediaViewerClose} onPress={() => setShowAvatarViewer(false)}>
            <Ionicons name="close" size={30} color="#fff" />
          </TouchableOpacity>
          {profile.avatar_url && <SafeImage uri={profile.avatar_url} style={styles.mediaViewerImage} resizeMode="contain" />}
        </View>
      </Modal>

      {/* Creer un post modal */}
      <Modal visible={showCreatePost} transparent animationType="slide" onRequestClose={() => setShowCreatePost(false)}>
        <KeyboardAvoidingView
          style={styles.cpOverlay}
          behavior={
            Platform.OS === 'ios'
              ? 'padding'
              : Platform.OS === 'android'
              ? 'height'
              : undefined
          }
          keyboardVerticalOffset={Platform.OS === 'android' ? 80 : 0}
        >
          <View style={styles.cpCard}>
            <View style={styles.cpHeader}>
              <Text style={styles.cpTitle}>{t('profileScreen.createPost.title')}</Text>
              <TouchableOpacity onPress={() => { setShowCreatePost(false); setPostImageUrl(null); setPostVideoUrl(null); setPostContent(''); }}>
                <Ionicons name="close" size={24} color="#666" />
              </TouchableOpacity>
            </View>

            <ScrollView
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.cpScrollContent}
            >
              <View style={styles.cpAuthorRow}>
                <SafeImage uri={profile.avatar_url} style={styles.cpAvatar} resizeMode="cover"
                  fallback={<View style={[styles.cpAvatar, styles.cpAvatarFallback]}><Text style={styles.cpAvatarText}>{getInitials(profile.nom, profile.prenom)}</Text></View>}
                />
                <View>
                  <Text style={styles.cpAuthorName}>{profile.prenom} {profile.nom}</Text>
                  {isPro && <View style={styles.cpProBadge}><Text style={styles.cpProBadgeText}>⭐ PRO</Text></View>}
                  <View style={styles.cpVisibilityRow}>
                    {visibilitiesAllowed.map(v => (
                      <TouchableOpacity key={v} style={[styles.cpVisibilityBtn, postVisibility === v && styles.cpVisibilityBtnActive]} onPress={() => setPostVisibility(v)}>
                        <Ionicons name={v === 'public' ? 'globe-outline' : v === 'friends' ? 'people-outline' : v === 'pro' ? 'star' : 'lock-closed-outline'} size={12} color={postVisibility === v ? '#fff' : '#666'} />
                        <Text style={[styles.cpVisibilityText, postVisibility === v && styles.cpVisibilityTextActive]}>
                          {v === 'public' ? t('profileScreen.createPost.visibility.public') : v === 'friends' ? t('profileScreen.createPost.visibility.friends') : v === 'pro' ? t('profileScreen.createPost.visibility.pro') : t('profileScreen.createPost.visibility.private')}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>

              <TextInput
                style={styles.cpInput}
                placeholder={t('profileScreen.createPost.placeholder')}
                placeholderTextColor="#bbb"
                value={postContent}
                onChangeText={setPostContent}
                multiline maxLength={1000} autoFocus
              />
              <Text style={styles.cpCharCount}>{postContent.length}/1000</Text>

              {postImageUrl && (
                <View style={styles.cpMediaContainer}>
                  <SafeImage uri={postImageUrl} style={styles.cpMediaPreview} resizeMode="cover" />
                  <TouchableOpacity style={styles.cpMediaRemove} onPress={() => setPostImageUrl(null)}>
                    <Ionicons name="close-circle" size={24} color="#EF4444" />
                  </TouchableOpacity>
                </View>
              )}
              {postVideoUrl && (
                <View style={styles.cpMediaContainer}>
                  <View style={[styles.cpMediaPreview, styles.cpVideoPreviewBg]}>
                    <Ionicons name="play-circle" size={40} color="rgba(255,255,255,0.9)" />
                    <Text style={styles.cpVideoPreviewLabel}>{t('profileScreen.createPost.videoReady')}</Text>
                  </View>
                  <TouchableOpacity style={styles.cpMediaRemove} onPress={() => setPostVideoUrl(null)}>
                    <Ionicons name="close-circle" size={24} color="#EF4444" />
                  </TouchableOpacity>
                </View>
              )}
              {isPro && !postImageUrl && !postVideoUrl && (
                <View style={styles.cpMediaButtons}>
                  <TouchableOpacity style={styles.cpMediaBtn} onPress={() => handlePickMedia('image')} disabled={uploadingMedia}>
                    {uploadingMedia ? <ActivityIndicator size="small" color={PURPLE} /> : <><Ionicons name="image-outline" size={20} color={PURPLE} /><Text style={styles.cpMediaBtnText}>{t('profileScreen.createPost.photo')}</Text></>}
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.cpMediaBtn} onPress={() => handlePickMedia('video')} disabled={uploadingMedia}>
                    {uploadingMedia ? <ActivityIndicator size="small" color={PURPLE} /> : <><Ionicons name="videocam-outline" size={20} color={PURPLE} /><Text style={styles.cpMediaBtnText}>{t('profileScreen.createPost.video')}</Text></>}
                  </TouchableOpacity>
                </View>
              )}

              <TouchableOpacity
                style={[styles.cpPublishBtn, !postContent.trim() && styles.cpPublishBtnDisabled]}
                onPress={handleCreatePost}
                disabled={creatingPost || !postContent.trim()}
                activeOpacity={0.8}
              >
                {creatingPost
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <><Ionicons name="send" size={18} color="#fff" /><Text style={styles.cpPublishBtnText}>{t('profileScreen.createPost.publish')}</Text></>
                }
              </TouchableOpacity>
            </ScrollView>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Long press détails */}
      {selectedPost && (
        <Modal visible={!!selectedPost} transparent animationType="fade" onRequestClose={() => setSelectedPost(null)}>
          <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setSelectedPost(null)}>
            <View style={styles.modalContent}>
              <Text style={styles.modalTitle}>{t('profileScreen.postDetails.title')}</Text>
              <View style={styles.modalInfo}><Text style={styles.modalLabel}>{t('profileScreen.postDetails.author')}</Text><Text style={styles.modalValue}>{selectedPost.author.prenom} {selectedPost.author.nom}</Text></View>
              <View style={styles.modalInfo}><Text style={styles.modalLabel}>{t('profileScreen.postDetails.published')}</Text><Text style={styles.modalValue}>{formatTimeAgo(selectedPost.created_at)}</Text></View>
              <View style={styles.modalInfo}><Text style={styles.modalLabel}>{t('profileScreen.postDetails.reactions')}</Text><Text style={styles.modalValue}>👍 {selectedPost.reactions.likes} · 💬 {selectedPost.reactions.comments} · 🔄 {selectedPost.reactions.shares}</Text></View>
              <TouchableOpacity style={styles.modalBtn} onPress={() => setSelectedPost(null)}><Text style={styles.modalBtnText}>{t('profileScreen.postDetails.close')}</Text></TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      )}

      {/* ScrollView principal */}
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
        refreshControl={
          Platform.OS !== 'web' ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={PURPLE}
              colors={[PURPLE, ACCENT]}
            />
          ) : undefined
        }
      >

        {toast && (
          <View style={[styles.toast, styles[`toast_${toast.type}` as keyof typeof styles] as any]}>
            <Ionicons name={toast.type === 'success' ? 'checkmark-circle' : toast.type === 'error' ? 'close-circle' : 'information-circle'} size={20} color="#fff" />
            <Text style={styles.toastText}>{toast.text}</Text>
          </View>
        )}

        {/* Header */}
        <LinearGradient colors={[PURPLE, PURPLE_DARK]} style={styles.header}>
          <TouchableOpacity style={styles.logoutBtn} onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); setShowLogoutModal(true); }}>
            <Ionicons name="log-out-outline" size={24} color="rgba(255,255,255,0.8)" />
          </TouchableOpacity>
          
          {/* Bouton Refresh pour le Web */}
          {Platform.OS === 'web' && (
            <TouchableOpacity style={styles.webRefreshBtn} onPress={onRefresh} disabled={refreshing} activeOpacity={0.7}>
              {refreshing ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Ionicons name="refresh" size={24} color="rgba(255,255,255,0.8)" />
              )}
            </TouchableOpacity>
          )}

          {isAdmin && (
            <TouchableOpacity style={styles.adminInfoBtn} onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); setShowAdminInfo(true); }} activeOpacity={0.75}>
              <View style={styles.adminInfoBtnInner}>
                <Ionicons name="shield-checkmark-outline" size={20} color={BRAND_GOLD} />
              </View>
            </TouchableOpacity>
          )}
          <View style={styles.avatarWrapper}>
            <TouchableOpacity onPress={() => profile.avatar_url && setShowAvatarViewer(true)} activeOpacity={profile.avatar_url ? 0.8 : 1}>
              <SafeImage uri={profile.avatar_url} style={styles.avatar} resizeMode="cover"
                fallback={<View style={[styles.avatar, { backgroundColor: '#6A0DAD', justifyContent: 'center', alignItems: 'center' }]}><Text style={{ color: '#fff', fontSize: 36, fontWeight: 'bold' }}>{getInitials(profile.nom, profile.prenom)}</Text></View>}
              />
            </TouchableOpacity>
            <TouchableOpacity style={styles.cameraBtn} onPress={pickAndUploadAvatar} disabled={uploadingAvatar}>
              {uploadingAvatar ? <ActivityIndicator size="small" color="#fff" /> : <Ionicons name="camera" size={18} color="#fff" />}
            </TouchableOpacity>
          </View>
          <Text style={styles.headerName}>{profile.prenom} {profile.nom}</Text>
          {isAdmin ? (
            <View style={styles.headerAdminBadge}>
              <Ionicons name="shield-checkmark" size={12} color={BRAND_GOLD} />
              <Text style={styles.headerAdminText}>{profile.role === 'supreme' ? t('profileScreen.roles.supreme') : profile.role === 'adminpro' ? t('profileScreen.roles.adminPro') : t('profileScreen.roles.admin')}</Text>
            </View>
          ) : isPro ? (
            <View style={styles.headerProBadge}><Text style={styles.headerProText}>{t('profileScreen.roles.pro')}</Text></View>
          ) : null}
          <Text style={styles.headerSub}>{t('profileScreen.memberSince', { date: new Date(profile.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) })}</Text>
        </LinearGradient>

        {/* Stats */}
        <View style={styles.statsRow}>
          <LinearGradient colors={['#10B981', '#34D399']} style={styles.statCard}>
            <Ionicons name="wallet-outline" size={26} color="#fff" />
            <Text style={styles.statValue}>{profile.solde_cfa.toLocaleString()}</Text>
            <Text style={styles.statLabel}>{t('profileScreen.stats.cfa')}</Text>
          </LinearGradient>
          <LinearGradient colors={[BRAND_GOLD, '#FF8C00']} style={styles.statCard}>
            <Ionicons name="trophy-outline" size={26} color="#fff" />
            <Text style={styles.statValue}>{profile.trophies_count}</Text>
            <Text style={styles.statLabel}>{t('profileScreen.stats.trophies')}</Text>
          </LinearGradient>
        </View>

        {/* Section Mon compte */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>{t('profileScreen.account.sectionLabel')}</Text>
          <TouchableOpacity style={styles.accountBtn} onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light); router.push('/recharge'); }} activeOpacity={0.85}>
            <LinearGradient colors={[DARK_BG, CARD_BG]} style={styles.accountBtnGrad} start={{ x:0,y:0 }} end={{ x:1,y:0 }}>
              <View style={[styles.accountBtnIconBox, { backgroundColor: BRAND_GOLD+'22', borderColor: BRAND_GOLD+'40' }]}>
                <MaterialCommunityIcons name="wallet-plus-outline" size={20} color={BRAND_GOLD} />
              </View>
              <View style={styles.accountBtnText}>
                <Text style={styles.accountBtnTitle}>{t('profileScreen.account.topUp.title')}</Text>
                <Text style={styles.accountBtnSub}>{t('profileScreen.account.topUp.sub')}</Text>
              </View>
              <View style={[styles.accountBtnArrow, { backgroundColor: BRAND_GOLD+'15' }]}>
                <Ionicons name="arrow-forward" size={15} color={BRAND_GOLD} />
              </View>
            </LinearGradient>
          </TouchableOpacity>
          {ACCOUNT_BTNS.map(btn => (
            <TouchableOpacity key={btn.title} style={styles.accountBtn} onPress={btn.onPress} activeOpacity={0.85}>
              <LinearGradient colors={[DARK_BG, CARD_BG]} style={styles.accountBtnGrad} start={{ x:0,y:0 }} end={{ x:1,y:0 }}>
                <View style={[styles.accountBtnIconBox, { backgroundColor: btn.bgColor, borderColor: btn.color+'40' }]}>
                  {btn.iconLib === 'MaterialCommunityIcons'
                    ? <MaterialCommunityIcons name={btn.icon as any} size={20} color={btn.color} />
                    : <Ionicons name={btn.icon as any} size={20} color={btn.color} />
                  }
                </View>
                <View style={styles.accountBtnText}>
                  <Text style={styles.accountBtnTitle}>{btn.title}</Text>
                  <Text style={styles.accountBtnSub}>{btn.sub}</Text>
                </View>
                <View style={[styles.accountBtnArrow, { backgroundColor: btn.color+'15' }]}>
                  <Ionicons name="arrow-forward" size={15} color={btn.color} />
                </View>
              </LinearGradient>
            </TouchableOpacity>
          ))}
        </View>

        {/* Infos lecture seule */}
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>{t('profileScreen.info.sectionLabel')}</Text>
          <View style={styles.infoCard}>
            <InfoRow icon="person-outline"   label={t('profileScreen.info.lastName')}  value={profile.nom} />
            <Separator />
            <InfoRow icon="person-outline"   label={t('profileScreen.info.firstName')} value={profile.prenom} />
            <Separator />
            <InfoRow icon="calendar-outline" label={t('profileScreen.info.birthDate')} value={profile.date_naissance ? formatDateReadable(profile.date_naissance) : '—'} />
          </View>
        </View>

        {/* Publications */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>{t('profileScreen.posts.title', { count: posts.length })}</Text>
            <TouchableOpacity style={styles.newPostBtn} onPress={() => { if (NATIVE) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium); setShowCreatePost(true); }}>
              <LinearGradient colors={['#FF0080', '#FF8C00']} style={styles.newPostGrad}>
                <Ionicons name="add" size={16} color="#fff" />
                <Text style={styles.newPostText}>{t('profileScreen.posts.publishButton')}</Text>
              </LinearGradient>
            </TouchableOpacity>
          </View>
          {loadingPosts ? (
            <View style={styles.postsLoading}><ActivityIndicator size="small" color={PURPLE} /></View>
          ) : posts.length === 0 ? (
            <View style={styles.emptyPosts}>
              <Ionicons name="newspaper-outline" size={48} color="#DDD" />
              <Text style={styles.emptyPostsText}>{t('profileScreen.posts.emptyTitle')}</Text>
              <Text style={styles.emptyPostsSub}>{t('profileScreen.posts.emptySub')}</Text>
            </View>
          ) : (
            posts.map(post => (
              <ProfilePostCard
                key={post.id} post={post} userId={userId} isPro={isPro}
                onLike={handleLike} onShare={handleShare} onSave={handleSave}
                onOpenComments={handleOpenComments} onOpenLikers={handleOpenLikers}
                onOpenSharers={handleOpenSharers} onOpenEdit={handleOpenEdit}
                onLongPress={handleLongPress}
              />
            ))
          )}
        </View>

      </ScrollView>

      <CommentsModal visible={showCommentsModal} postId={selectedPostForComments} userId={userId} onClose={() => { setShowCommentsModal(false); setSelectedPostForComments(null); }} onCommentAdded={() => loadPosts()} />
      <LikersModal visible={showLikersModal} postId={selectedPostForLikers} onClose={() => { setShowLikersModal(false); setSelectedPostForLikers(null); }} />
      <SharersModal visible={showSharersModal} postId={selectedPostForSharers} onClose={() => { setShowSharersModal(false); setSelectedPostForSharers(null); }} />
      <EditPostModal visible={showEditModal} postId={selectedPostForEdit?.id || null} userId={userId} initialContent={selectedPostForEdit?.content || ''} onClose={() => { setShowEditModal(false); setSelectedPostForEdit(null); }} onPostUpdated={async () => { await loadPosts(); }} />

      {/*
        LogoutModal :
        - `profile` et `refreshToken` sont désormais transmis pour que le
          modal puisse sauvegarder le compte (saveAccount) si l'utilisateur
          choisit de mémoriser.
        - `onLogoutSuccess` reçoit `wantsToRemember` et est le SEUL endroit
          qui déclenche router.replace (voir handleLogoutSuccess ci-dessus).
      */}
      <LogoutModal
        visible={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        profile={{
          id: profile.id,
          nom: profile.nom,
          prenom: profile.prenom,
          avatar_url: profile.avatar_url,
        }}
        refreshToken={refreshTokenRef.current}
        onLogoutSuccess={handleLogoutSuccess}
      />

      {isAdmin && <AdminInfoModal visible={showAdminInfo} userId={userId} accessToken={accessTokenRef.current} onClose={() => setShowAdminInfo(false)} />}

      <LimitNotification visible={limitNotif} message={limitNotifMessage} isPro={isPro} onDismiss={() => setLimitNotif(false)} />

    </KeyboardAvoidingView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container:     { flex: 1, backgroundColor: '#F5F5F5' },
  scrollContent: { paddingBottom: 100 },
  centered:      { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F5F5F5', padding: 20 },
  loadingText:   { marginTop: 16, fontSize: 16, color: '#666' },
  errorText:     { marginTop: 16, fontSize: 16, color: '#EF4444', textAlign: 'center' },
  retryButton:   { marginTop: 20, paddingHorizontal: 24, paddingVertical: 12, backgroundColor: PURPLE, borderRadius: 10 },
  retryButtonText: { color: '#fff', fontSize: 16, fontWeight: '600' },

  toast:         { position: 'absolute', top: Platform.OS === 'ios' ? 55 : 15, left: 20, right: 20, zIndex: 100, flexDirection: 'row', alignItems: 'center', padding: 14, borderRadius: 12, gap: 10 },
  toast_success: { backgroundColor: '#10B981' },
  toast_error:   { backgroundColor: '#EF4444' },
  toast_info:    { backgroundColor: '#3B82F6' },
  toastText:     { flex: 1, color: '#fff', fontSize: 14, fontWeight: '600' },

  header:           { paddingTop: Platform.OS === 'ios' ? 64 : 44, paddingBottom: 36, alignItems: 'center', position: 'relative' },
  logoutBtn:        { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 20, padding: 6 },
  webRefreshBtn:    { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 60, padding: 6 },
  adminInfoBtn:     { position: 'absolute', top: Platform.OS === 'ios' ? 58 : 38, left: 16, padding: 4 },
  adminInfoBtnInner:{ width: 36, height: 36, borderRadius: 11, backgroundColor: 'rgba(255,215,0,0.15)', borderWidth: 1, borderColor: 'rgba(255,215,0,0.35)', justifyContent: 'center', alignItems: 'center' },
  avatarWrapper:    { position: 'relative', marginBottom: 14 },
  avatar:           { width: 110, height: 110, borderRadius: 55, borderWidth: 4, borderColor: '#fff', backgroundColor: '#E0E0E0' },
  cameraBtn:        { position: 'absolute', bottom: 2, right: 2, width: 36, height: 36, borderRadius: 18, backgroundColor: '#FF8C00', justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#fff' },
  headerName:       { fontSize: 24, fontWeight: 'bold', color: '#fff', marginBottom: 4 },
  headerProBadge:   { backgroundColor: 'rgba(255,215,0,0.2)', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 4, marginBottom: 6 },
  headerProText:    { color: BRAND_GOLD, fontSize: 12, fontWeight: '700' },
  headerAdminBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,215,0,0.18)', borderWidth: 1, borderColor: 'rgba(255,215,0,0.35)', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 4, marginBottom: 6 },
  headerAdminText:  { color: BRAND_GOLD, fontSize: 12, fontWeight: '800' },
  headerSub:        { fontSize: 13, color: 'rgba(255,255,255,0.75)' },

  statsRow:  { flexDirection: 'row', gap: 12, paddingHorizontal: 20, marginTop: 20, marginBottom: 4 },
  statCard:  { flex: 1, borderRadius: 16, padding: 20, alignItems: 'center', gap: 4 },
  statValue: { fontSize: 22, fontWeight: 'bold', color: '#fff', marginTop: 4 },
  statLabel: { fontSize: 13, color: 'rgba(255,255,255,0.9)' },

  section:       { paddingHorizontal: 16, marginTop: 20, gap: 10 },
  sectionLabel:  { fontSize: 12, color: '#9491B4', fontWeight: '700', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 2 },
  sectionHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionTitle:  { fontSize: 17, fontWeight: 'bold', color: '#2D0072' },
  newPostBtn:    {},
  newPostGrad:   { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20 },
  newPostText:   { color: '#fff', fontSize: 13, fontWeight: '700' },

  accountBtn:       { borderRadius: 16, overflow: 'hidden', borderWidth: 1, borderColor: CARD_BORDER, shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.12, shadowRadius: 8, elevation: 3 },
  accountBtnGrad:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  accountBtnIconBox:{ width: 42, height: 42, borderRadius: 12, borderWidth: 1, justifyContent: 'center', alignItems: 'center' },
  accountBtnText:   { flex: 1, marginLeft: 12 },
  accountBtnTitle:  { fontSize: 15, fontWeight: '700', color: TEXT_MAIN },
  accountBtnSub:    { fontSize: 11, color: TEXT_SUB, marginTop: 2 },
  accountBtnArrow:  { width: 32, height: 32, borderRadius: 10, justifyContent: 'center', alignItems: 'center' },

  infoCard:      { backgroundColor: '#fff', borderRadius: 16, padding: 16, shadowColor: PURPLE, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 8, elevation: 3 },
  infoRow:       { flexDirection: 'row', alignItems: 'center', paddingVertical: 10 },
  infoIconBox:   { width: 40, height: 40, borderRadius: 20, backgroundColor: '#F0E6FF', justifyContent: 'center', alignItems: 'center', marginRight: 14 },
  infoLabel:     { fontSize: 12, color: '#999', marginBottom: 2 },
  infoValue:     { fontSize: 15, fontWeight: '600', color: '#222' },
  separatorLine: { height: 1, backgroundColor: '#F5F0FF', marginLeft: 54 },

  postsLoading:   { paddingVertical: 30, alignItems: 'center' },
  emptyPosts:     { alignItems: 'center', paddingVertical: 40, gap: 8 },
  emptyPostsText: { fontSize: 16, color: '#999', fontWeight: '600' },
  emptyPostsSub:  { fontSize: 13, color: '#bbb' },

  postCard:          { backgroundColor: '#fff', marginVertical: 6, borderRadius: 14, paddingVertical: 14 },
  postHeader:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, marginBottom: 10 },
  postAuthor:        { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 10 },
  postAvatar:        { width: 40, height: 40, borderRadius: 20 },
  avatarPlaceholder: { width: 40, height: 40, borderRadius: 20, backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  avatarText:        { color: '#fff', fontSize: 14, fontWeight: 'bold' },
  authorName:        { fontSize: 14, fontWeight: '600', color: '#1A1A1A' },
  postTime:          { fontSize: 12, color: '#999', marginTop: 1 },
  postContent:       { fontSize: 14, color: '#333', lineHeight: 20, paddingHorizontal: 14, marginBottom: 10 },
  postImage:         { width: '100%', height: 260, marginBottom: 10 },
  videoThumb:        { width: '100%', height: 180, backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', marginBottom: 10, gap: 8 },
  videoLabel:        { color: 'rgba(255,255,255,0.75)', fontSize: 13 },
  mediaExpandHint:   { position: 'absolute', bottom: 18, right: 10, backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: 12, padding: 4 },

  statsBar:        { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 10 },
  statsLikeCircle: { backgroundColor: PURPLE, width: 18, height: 18, borderRadius: 9, justifyContent: 'center', alignItems: 'center' },
  statsText:       { fontSize: 13, color: '#65676B' },
  statsSharesLink: { color: PURPLE, fontWeight: '600' },
  statsRight:      { flexDirection: 'row', alignItems: 'center', gap: 5 },
  statsSep:        { color: '#CCC' },

  actionsBar:          { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: '#CED0D4', paddingTop: 4, paddingBottom: 4, marginHorizontal: 12, marginTop: 4 },
  actionBtnModern:     { flex: 1, paddingVertical: 8, borderRadius: 8 },
  actionContentModern: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  likeBtnInner:        { borderRadius: 8, paddingVertical: 4, paddingHorizontal: 8 },
  likeBtnActive:       { shadowColor: PURPLE, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.35, shadowRadius: 8, elevation: 4 },

  mediaViewerOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.97)', justifyContent: 'center', alignItems: 'center' },
  mediaViewerClose:   { position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, right: 20, zIndex: 10, padding: 8, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20 },
  mediaViewerImage:   { width: SCREEN_W, height: SCREEN_H * 0.85 },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center' },
  modalContent: { backgroundColor: '#fff', borderRadius: 20, padding: 25, width: SCREEN_W * 0.85, maxWidth: 400 },
  modalTitle:   { fontSize: 18, fontWeight: 'bold', color: '#333', marginBottom: 20, textAlign: 'center' },
  modalInfo:    { marginBottom: 12 },
  modalLabel:   { fontSize: 12, color: '#999', marginBottom: 3 },
  modalValue:   { fontSize: 14, color: '#333', fontWeight: '600' },
  modalBtn:     { backgroundColor: PURPLE, padding: 12, borderRadius: 10, alignItems: 'center', marginTop: 10 },
  modalBtnText: { color: '#fff', fontSize: 14, fontWeight: 'bold' },

  // ── Create post modal ────────────────────────────────────────────────────────
  cpOverlay:              { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  cpCard:                 { backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, paddingTop: 24, paddingHorizontal: 24, paddingBottom: Platform.OS === 'ios' ? 40 : 24, maxHeight: '90%' },
  cpScrollContent:        { paddingBottom: 8 },
  cpHeader:               { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cpTitle:                { fontSize: 18, fontWeight: 'bold', color: '#2D0072' },
  cpAuthorRow:            { flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 16 },
  cpAvatar:               { width: 44, height: 44, borderRadius: 22 },
  cpAvatarFallback:       { backgroundColor: '#8A2BE2', justifyContent: 'center', alignItems: 'center' },
  cpAvatarText:           { color: '#fff', fontSize: 16, fontWeight: 'bold' },
  cpAuthorName:           { fontSize: 15, fontWeight: '700', color: '#222', marginBottom: 4 },
  cpProBadge:             { backgroundColor: '#FFF8E1', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 2, marginBottom: 6, alignSelf: 'flex-start' },
  cpProBadgeText:         { color: '#F59E0B', fontSize: 11, fontWeight: '700' },
  cpVisibilityRow:        { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  cpVisibilityBtn:        { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 12, backgroundColor: '#F5F5F5', borderWidth: 1, borderColor: '#E0E0E0' },
  cpVisibilityBtnActive:  { backgroundColor: PURPLE, borderColor: PURPLE },
  cpVisibilityText:       { fontSize: 11, color: '#666', fontWeight: '600' },
  cpVisibilityTextActive: { color: '#fff' },
  cpInput:                { fontSize: 16, color: '#333', minHeight: 100, textAlignVertical: 'top', paddingVertical: 12, marginBottom: 8 },
  cpCharCount:            { fontSize: 12, color: '#bbb', textAlign: 'right', marginBottom: 12 },
  cpMediaButtons:         { flexDirection: 'row', gap: 10, marginBottom: 16 },
  cpMediaBtn:             { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 8, backgroundColor: '#F0E6FF', borderRadius: 12 },
  cpMediaBtnText:         { fontSize: 13, color: PURPLE, fontWeight: '600' },
  cpMediaContainer:       { position: 'relative', marginBottom: 12 },
  cpMediaPreview:         { width: '100%', height: 180, borderRadius: 12, backgroundColor: '#F0F0F0' },
  cpVideoPreviewBg:       { backgroundColor: '#1A1A2E', justifyContent: 'center', alignItems: 'center', gap: 8 },
  cpVideoPreviewLabel:    { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  cpMediaRemove:          { position: 'absolute', top: 8, right: 8 },
  cpPublishBtn:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: PURPLE, borderRadius: 14, paddingVertical: 16, marginTop: 8 },
  cpPublishBtnDisabled:   { backgroundColor: '#C4B5FD' },
  cpPublishBtnText:       { color: '#fff', fontSize: 16, fontWeight: 'bold' },
});

// ─── Styles SettingsModal ─────────────────────────────────────────────────────
const sStyles = StyleSheet.create({
  container:     { flex: 1, marginTop: Platform.OS === 'ios' ? 40 : 20, borderTopLeftRadius: 24, borderTopRightRadius: 24, overflow: 'hidden', backgroundColor: DARK_BG },
  header:        { paddingTop: Platform.OS === 'ios' ? 50 : 36, paddingBottom: 28, alignItems: 'center', position: 'relative' },
  backBtn:       { position: 'absolute', top: Platform.OS === 'ios' ? 48 : 34, left: 20, width: 40, height: 40, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.15)', justifyContent: 'center', alignItems: 'center' },
  headerCenter:  { alignItems: 'center', gap: 6 },
  headerIconBox: { width: 54, height: 54, borderRadius: 16, backgroundColor: 'rgba(255,215,0,0.15)', borderWidth: 1, borderColor: 'rgba(255,215,0,0.3)', justifyContent: 'center', alignItems: 'center', marginBottom: 4 },
  headerTitle:   { fontSize: 20, fontWeight: '800', color: '#fff', letterSpacing: 0.3 },
  headerSub:     { fontSize: 13, color: 'rgba(255,255,255,0.7)' },
  toast:         { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: '#EF4444', marginHorizontal: 16, marginTop: 10, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10 },
  toastText:     { flex: 1, fontSize: 13, color: '#fff', fontWeight: '600' },
  scroll:        { paddingHorizontal: 20, paddingTop: 24, paddingBottom: 60, gap: 16 },
  fieldBlock:    { gap: 8 },
  fieldLabel:    { fontSize: 12, color: TEXT_SUB, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase' },
  inputRow:      { flexDirection: 'row', alignItems: 'center', backgroundColor: CARD_BG, borderRadius: 14, borderWidth: 1.5, borderColor: CARD_BORDER, overflow: 'hidden' },
  inputIconBox:  { width: 48, height: 52, justifyContent: 'center', alignItems: 'center', borderRightWidth: 1, borderRightColor: CARD_BORDER },
  input:         { flex: 1, fontSize: 15, color: TEXT_MAIN, paddingHorizontal: 14, paddingVertical: 15 },
  actions:       { flexDirection: 'row', gap: 12, marginTop: 8 },
  cancelBtn:     { flex: 1, paddingVertical: 15, borderRadius: 14, borderWidth: 1.5, borderColor: CARD_BORDER, alignItems: 'center' },
  cancelText:    { fontSize: 15, color: TEXT_SUB, fontWeight: '600' },
  saveBtn:       { flex: 1, borderRadius: 14, overflow: 'hidden' },
  saveBtnGrad:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 15 },
  saveText:      { fontSize: 15, color: '#fff', fontWeight: '800' },

  // ── Sélecteur de langue ──────────────────────────────────────────────────
  languageIntro: { fontSize: 13, color: TEXT_SUB, lineHeight: 19, marginTop: -2, marginBottom: 4 },
  languageList: {
    backgroundColor: CARD_BG, borderRadius: 14, borderWidth: 1.5, borderColor: CARD_BORDER,
    overflow: 'hidden',
  },
  languageRow: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    paddingHorizontal: 14, paddingVertical: 14,
  },
  languageRowBorder: { borderBottomWidth: 1, borderBottomColor: CARD_BORDER },
  languageRowActive: { backgroundColor: 'rgba(255,215,0,0.06)' },
  languageFlag:       { fontSize: 26 },
  languageName:       { fontSize: 15, fontWeight: '700', color: TEXT_MAIN, marginBottom: 2 },
  languageNameActive: { color: BRAND_GOLD },
  languageNative:     { fontSize: 12, color: TEXT_SUB },
  languageCheckBox: {
    width: 24, height: 24, borderRadius: 12, backgroundColor: BRAND_GOLD,
    justifyContent: 'center', alignItems: 'center',
  },
  languageCheckBoxEmpty: {
    width: 24, height: 24, borderRadius: 12, borderWidth: 1.5, borderColor: CARD_BORDER,
  },

  // ── Carte statut en ligne (design seul, aucun texte) ─────────────────────
  statusCard: {
    flexDirection: 'row', alignItems: 'center', gap: 16,
    backgroundColor: CARD_BG, borderRadius: 14, borderWidth: 1.5, borderColor: CARD_BORDER,
    paddingHorizontal: 16, paddingVertical: 14,
  },
  statusOrbWrap: {
    width: 44, height: 44, justifyContent: 'center', alignItems: 'center',
  },
  statusHalo: {
    position: 'absolute', width: 22, height: 22, borderRadius: 11, backgroundColor: SUCCESS,
  },
  statusOrb: {
    width: 22, height: 22, borderRadius: 11, borderWidth: 3,
    justifyContent: 'center', alignItems: 'center',
  },
  statusOrbOn: {
    backgroundColor: SUCCESS, borderColor: 'rgba(0,229,160,0.35)',
  },
  statusOrbOff: {
    backgroundColor: CARD_BORDER, borderColor: 'rgba(148,145,180,0.35)',
  },
  statusSlash: {
    position: 'absolute', width: 32, height: 2.5, borderRadius: 2,
    backgroundColor: TEXT_SUB, transform: [{ rotate: '-45deg' }],
  },
  statusBars: {
    flex: 1, flexDirection: 'row', alignItems: 'flex-end', gap: 5, height: 28,
  },
  statusBar: {
    width: 6, borderRadius: 3,
  },
  statusBarOn: {
    backgroundColor: SUCCESS,
  },
  statusBarOff: {
    backgroundColor: CARD_BORDER,
  },
  statusBtn: {
    width: 48, height: 48, borderRadius: 24, borderWidth: 1.5,
    justifyContent: 'center', alignItems: 'center',
  },
  statusBtnOn: {
    backgroundColor: 'rgba(0,229,160,0.12)', borderColor: 'rgba(0,229,160,0.4)',
  },
  statusBtnOff: {
    backgroundColor: 'rgba(148,145,180,0.1)', borderColor: CARD_BORDER,
  },
  statusCardLoading: {
    opacity: 0.55,
  },
});
